/**
 * /admin/agent-feedback — سجلّ تقييمات مساعد الأدمن.
 * يعرض كلّ سؤال + جواب + تقييم (👍/👎/بلا) + ملاحظة + المصدر.
 * يحسب دقّة عامّة + يفلتر «الأجوبة الخاطئة فقط» لتسهيل المراجعة.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { listAdminAgentFeedback } from "@/lib/admin-agent.functions";
import { useAdminGate } from "@/hooks/useAdminGate";
import { ThumbsUp, ThumbsDown, MinusCircle, MessageSquare } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { useAdminTexts } from "@/lib/useAdminTexts";


export const Route = createFileRoute("/admin/agent-feedback")({
  component: AgentFeedbackPage,
  head: () => ({ meta: [{ title: "سجلّ تقييمات المساعد · Lecmo Admin" }] }),
  errorComponent: ({ error }) => (
    <div role="alert" className="p-4 text-sm text-destructive">
      خطأ: {error instanceof Error ? error.message : String(error)}
    </div>
  ),
  notFoundComponent: () => <div className="p-4">الصفحة غير موجودة</div>,
});

function AgentFeedbackPage() {
  useAdminTexts();
  const { ready } = useAdminGate();
  const list = useServerFn(listAdminAgentFeedback);
  const [filter, setFilter] = useState<"all" | "helpful" | "wrong" | "unrated">("all");

  const { data = [], isLoading, error } = useQuery({
    queryKey: ["admin-agent-feedback"],
    queryFn: () => list(),
    enabled: ready,
    refetchInterval: 60_000,
  });

  if (!ready) return <SiteShell><div className="p-4 text-sm text-muted-foreground">جارٍ التحقّق…</div></SiteShell>;
  if (isLoading) return <SiteShell><div className="p-4 text-sm text-muted-foreground">جارٍ التحميل…</div></SiteShell>;
  if (error) return <SiteShell><div className="p-4 text-sm text-destructive">{(error as Error).message}</div></SiteShell>;


  const total = data.length;
  const helpful = data.filter((r) => r.was_helpful === true).length;
  const wrong = data.filter((r) => r.was_helpful === false).length;
  const unrated = data.filter((r) => r.was_helpful === null).length;
  const rated = helpful + wrong;
  const accuracy = rated > 0 ? Math.round((helpful / rated) * 100) : null;

  const filtered = data.filter((r) => {
    if (filter === "helpful") return r.was_helpful === true;
    if (filter === "wrong") return r.was_helpful === false;
    if (filter === "unrated") return r.was_helpful === null;
    return true;
  });

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 space-y-4 sm:px-6">

      <header className="space-y-1">
        <h1 className="text-xl font-bold">سجلّ تقييمات مساعد الأدمن</h1>
        <p className="text-xs text-muted-foreground">
          كلّ سؤال طُرح على المساعد + جوابه + تقييمك. راجع الأجوبة الخاطئة وحدّث
          <code className="mx-1 px-1 bg-muted rounded">docs/features-map.md</code> لتصحيح المعرفة.
        </p>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="إجماليّ" value={total} />
        <Kpi label="مفيد 👍" value={helpful} tone="success" />
        <Kpi label="خاطئ 👎" value={wrong} tone="danger" />
        <Kpi
          label="الدقّة"
          value={accuracy === null ? "—" : `${accuracy}%`}
          tone={accuracy !== null && accuracy >= 80 ? "success" : "warning"}
        />
      </div>

      {/* فلاتر */}
      <div className="flex flex-wrap gap-1.5 text-xs">
        {[
          { id: "all", label: `الكلّ (${total})` },
          { id: "helpful", label: `مفيد (${helpful})` },
          { id: "wrong", label: `خاطئ (${wrong})` },
          { id: "unrated", label: `بلا تقييم (${unrated})` },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id as typeof filter)}
            className={`px-2.5 py-1 rounded-full border min-h-[36px] ${
              filter === f.id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background hover:bg-muted"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* القائمة */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-none">
            لا توجد عناصر في هذا الفلتر.
          </div>
        ) : (
          filtered.map((r) => (
            <article key={r.id} className="rounded-none border border-border bg-card p-3 space-y-2">
              <header className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                <span>{new Date(r.created_at).toLocaleString("ar-EG")}</span>
                <span className="flex items-center gap-2">
                  {r.user_email && <span>{r.user_email}</span>}
                  <RatingBadge rating={r.was_helpful} />
                </span>
              </header>

              <div className="space-y-1">
                <div className="text-[11px] font-semibold text-muted-foreground">السؤال:</div>
                <div className="text-sm">{r.question}</div>
              </div>

              <div className="space-y-1">
                <div className="text-[11px] font-semibold text-muted-foreground">الجواب:</div>
                <div className="text-sm whitespace-pre-wrap rounded bg-muted/50 p-2">{r.answer}</div>
              </div>

              {r.sources.length > 0 && (
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  {r.sources.map((s, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      📎 {s}
                    </span>
                  ))}
                </div>
              )}

              {r.note && (
                <div className="flex items-start gap-1 text-[11px] text-muted-foreground border-t border-border pt-2">
                  <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>{r.note}</span>
                </div>
              )}
            </article>
          ))
        )}
      </div>
      </div>
    </SiteShell>
  );
}


function Kpi({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "success" | "danger" | "warning";
}) {
  const toneClass =
    tone === "success"
      ? "text-green-700 dark:text-green-400"
      : tone === "danger"
        ? "text-red-700 dark:text-red-400"
        : tone === "warning"
          ? "text-amber-700 dark:text-amber-400"
          : "text-foreground";
  return (
    <div className="rounded-none border border-border bg-card p-2.5">
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}

function RatingBadge({ rating }: { rating: boolean | null }) {
  if (rating === true)
    return (
      <span className="inline-flex items-center gap-1 text-green-700">
        <ThumbsUp className="h-3 w-3" /> مفيد
      </span>
    );
  if (rating === false)
    return (
      <span className="inline-flex items-center gap-1 text-red-700">
        <ThumbsDown className="h-3 w-3" /> خاطئ
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <MinusCircle className="h-3 w-3" /> بلا تقييم
    </span>
  );
}
